import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { ChevronRight, DollarSign, LogOut, Menu, UserCircle, Wallet, X } from 'lucide-react';
import { SupportDownloadRail, MONEY, ACTION, PORTAL_NAME, formatCreditsWithUnit } from '@kioskgaming/ui';
import { useAuth } from '../../hooks/useAuth';
import { AGENT_AUTH_TOKEN_CHANGED_EVENT } from '../../constants';
import { fetchDashboard } from '../../services/api';
import AgentNotificationBell from '../AgentNotificationBell';
import AgentInAppPopupGate from '../AgentInAppPopupGate';
import VerifyNowTrigger from '../VerifyNowTrigger';
import { getSuperAgentBreadcrumbTail } from '../../utils/superAgentBreadcrumb';
import { NAV_ITEMS, type NavItem } from '../../config/nav';
import vaultPlayLogo from '../../assets/brand/vault-play-logo.png';

function SidebarBalanceCards({
  creditBalance,
  usdBalance,
  loading,
}: {
  creditBalance: number | null;
  usdBalance: number | null;
  loading: boolean;
}) {
  return (
    <div className="space-y-2">
      <div className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm shadow-sm">
        <div className="flex min-w-0 items-start gap-2">
          <Wallet className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden />
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{MONEY.creditBalance}</p>
            <p className="mt-0.5 text-base font-bold tabular-nums text-gray-900">
              {loading ? '…' : formatCreditsWithUnit(creditBalance)}
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm shadow-sm">
        <div className="flex min-w-0 items-start gap-2">
          <DollarSign className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden />
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{MONEY.usdWalletBalance}</p>
            <p className="mt-0.5 text-base font-bold tabular-nums text-gray-900">
              {loading ? '…' : `$${Number(usdBalance ?? 0).toFixed(2)}`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function isPathActive(pathname: string, item: NavItem) {
  if (item.matchPrefix) return pathname === item.matchPrefix || pathname.startsWith(`${item.matchPrefix}/`);
  if (item.end) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

function SidebarNavLink({
  to,
  end,
  matchPrefix,
  icon: Icon,
  children,
  onClick,
}: {
  to: string;
  end?: boolean;
  matchPrefix?: string;
  icon: LucideIcon;
  children: ReactNode;
  onClick?: () => void;
}) {
  const location = useLocation();
  const active = matchPrefix
    ? location.pathname === matchPrefix || location.pathname.startsWith(`${matchPrefix}/`)
    : end
      ? location.pathname === to
      : location.pathname === to || location.pathname.startsWith(`${to}/`);

  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
        active ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
      }`}
    >
      <Icon className={`h-[18px] w-[18px] shrink-0 ${active ? 'text-gray-900' : 'text-gray-500'}`} strokeWidth={1.5} />
      <span>{children}</span>
    </NavLink>
  );
}

export default function AppLayout({
  children,
  contentFullWidth = true,
}: {
  children: ReactNode;
  /** Full main column width (default). Pass false for a centered max-w-6xl column. */
  contentFullWidth?: boolean;
}) {
  const telegramSupportUrl = process.env.REACT_APP_CLIENT_TELEGRAM_SUPPORT_URL?.trim() || undefined;
  const androidAppUrl = process.env.REACT_APP_CLIENT_ANDROID_APK_URL?.trim() || undefined;
  const iosGuideUrl = process.env.REACT_APP_CLIENT_IOS_VIDEO_URL?.trim() || undefined;
  const { user, logout } = useAuth();
  const location = useLocation();
  const [accountOpen, setAccountOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [popupNavAllowed, setPopupNavAllowed] = useState<boolean | null>(null);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [usdBalance, setUsdBalance] = useState<number | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  const loadPopupNav = useCallback(async () => {
    if (!user?.id) {
      setPopupNavAllowed(null);
      setCreditBalance(null);
      setUsdBalance(null);
      setBalanceLoading(false);
      return;
    }
    setBalanceLoading(true);
    try {
      const res = await fetchDashboard();
      if (res.success && res.data) {
        setPopupNavAllowed(!!res.data.can_delegate_popups);
        setCreditBalance(res.data.credit_balance);
        setUsdBalance(res.data.online_balance_net ?? null);
      } else {
        setPopupNavAllowed(false);
        setCreditBalance(null);
        setUsdBalance(null);
      }
    } catch {
      setPopupNavAllowed(false);
      setCreditBalance(null);
      setUsdBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (typeof user?.can_delegate_popups === 'boolean') {
      setPopupNavAllowed(user.can_delegate_popups);
    }
  }, [user]);

  useEffect(() => {
    void loadPopupNav();
  }, [loadPopupNav]);

  useEffect(() => {
    const onAuth = () => {
      void loadPopupNav();
    };
    window.addEventListener(AGENT_AUTH_TOKEN_CHANGED_EVENT, onAuth);
    return () => window.removeEventListener(AGENT_AUTH_TOKEN_CHANGED_EVENT, onAuth);
  }, [loadPopupNav]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible' && user) void loadPopupNav();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [loadPopupNav, user]);

  const navItemsFiltered = useMemo(() => {
    if (popupNavAllowed !== true) {
      return NAV_ITEMS.filter((i) => i.to !== '/popups/send' && i.to !== '/broadcasts/send');
    }
    return NAV_ITEMS;
  }, [popupNavAllowed]);

  const primaryMobileNav = useMemo(() => navItemsFiltered.filter((item) => item.primary), [navItemsFiltered]);

  const rootBreadcrumbLabel = user?.name || user?.email || 'Super Agent';
  const breadcrumbTail = useMemo(
    () => getSuperAgentBreadcrumbTail(location.pathname),
    [location.pathname],
  );

  const closeMenus = () => {
    setMobileMenuOpen(false);
    setAccountOpen(false);
  };

  const renderProfileMenu = () => (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => setAccountOpen((o) => !o)}
        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50"
        aria-expanded={accountOpen}
        aria-haspopup="true"
        aria-label="Account menu"
      >
        <UserCircle className="h-6 w-6" strokeWidth={1.5} aria-hidden />
      </button>
      {accountOpen && (
        <div className="absolute right-0 top-full z-50 mt-2 w-60 rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
          <p className="border-b border-gray-100 px-3 py-2 text-xs font-medium text-gray-900">
            {user?.name || 'Account'}
          </p>
          <p className="truncate px-3 py-2 text-xs text-gray-500">{user?.email}</p>
          <button
            type="button"
            onClick={() => {
              setAccountOpen(false);
              logout();
            }}
            className="flex w-full items-center gap-2 border-t border-gray-100 px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            {ACTION.signOut}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#fafafa] text-gray-900">
      <AgentInAppPopupGate />

      <aside className="hidden w-[280px] shrink-0 border-r border-gray-200 bg-white lg:flex lg:flex-col">
        <div className="flex flex-1 flex-col gap-6 p-5">
          <div className="flex items-center gap-2.5 px-1">
            <img src={vaultPlayLogo} alt="" className="h-9 w-9 shrink-0 rounded-lg object-cover" />
            <div className="leading-tight">
              <span className="block text-lg font-bold tracking-tight text-gray-900">Vault Play</span>
              <span className="text-xs font-medium text-gray-500">{PORTAL_NAME.super_agent}</span>
            </div>
          </div>

          <SidebarBalanceCards creditBalance={creditBalance} usdBalance={usdBalance} loading={balanceLoading} />

          <nav className="flex flex-1 flex-col gap-1 overflow-y-auto pr-1">
            {navItemsFiltered.map((item) => (
              <SidebarNavLink key={item.to} to={item.to} end={item.end} matchPrefix={item.matchPrefix} icon={item.icon}>
                {item.label}
              </SidebarNavLink>
            ))}
          </nav>
        </div>
      </aside>

      {mobileMenuOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/35 lg:hidden"
            aria-label="Close navigation menu"
            onClick={closeMenus}
          />
          <aside className="fixed inset-y-0 left-0 z-50 flex w-[86%] max-w-xs flex-col border-r border-gray-200 bg-white p-4 lg:hidden">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src={vaultPlayLogo} alt="" className="h-8 w-8 shrink-0 rounded-md object-cover" />
                <div className="leading-tight">
                  <div className="text-sm font-semibold text-gray-900">Vault Play</div>
                  <div className="text-xs text-gray-500">{PORTAL_NAME.super_agent}</div>
                </div>
              </div>
              <button
                type="button"
                className="rounded-lg p-2 text-gray-600 hover:bg-gray-100"
                onClick={closeMenus}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-3">
              <SidebarBalanceCards creditBalance={creditBalance} usdBalance={usdBalance} loading={balanceLoading} />
            </div>

            <nav className="mt-4 flex flex-1 flex-col gap-1 overflow-y-auto pb-4">
              {navItemsFiltered.map((item) => (
                <SidebarNavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  matchPrefix={item.matchPrefix}
                  icon={item.icon}
                  onClick={closeMenus}
                >
                  {item.label}
                </SidebarNavLink>
              ))}
            </nav>
          </aside>
        </>
      )}

      <main className="relative min-w-0 flex-1 pb-24 lg:pb-0">
        <header className="sticky top-0 z-20 border-b border-gray-200 bg-white/95 backdrop-blur lg:hidden">
          <div className="flex items-center justify-between gap-2 px-4 py-3 sm:px-6">
            <button
              type="button"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open navigation menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0 flex-1 text-center">
              <p className="truncate text-sm font-semibold text-gray-900">
                {user?.name || user?.email || PORTAL_NAME.super_agent}
              </p>
              <p className="text-xs text-gray-500">Super Agent</p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <VerifyNowTrigger />
              <AgentNotificationBell />
              {renderProfileMenu()}
            </div>
          </div>
        </header>

        <div
          className={
            contentFullWidth
              ? 'w-full max-w-none px-4 py-5 sm:px-6 lg:px-10 lg:py-8 lg:pr-20'
              : 'mx-auto max-w-6xl px-4 py-5 sm:px-6 lg:px-8 lg:py-8 lg:pr-20'
          }
        >
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <nav
              className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 text-sm text-gray-600"
              aria-label="Breadcrumb"
            >
              <span className="font-medium text-gray-900">{rootBreadcrumbLabel}</span>
              {breadcrumbTail.map((label) => (
                <span key={label} className="flex items-center gap-1.5">
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
                  <span className="text-gray-600">{label}</span>
                </span>
              ))}
            </nav>
            <div className="hidden shrink-0 items-center gap-2 lg:flex">
              <VerifyNowTrigger />
              <AgentNotificationBell />
              {renderProfileMenu()}
            </div>
          </div>
          {children}
        </div>
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white/95 px-2 pb-[max(env(safe-area-inset-bottom),0.5rem)] pt-2 shadow-[0_-8px_20px_rgba(0,0,0,0.06)] backdrop-blur lg:hidden">
        <div
          className="grid gap-1"
          style={{ gridTemplateColumns: `repeat(${Math.max(primaryMobileNav.length, 1)}, minmax(0, 1fr))` }}
        >
          {primaryMobileNav.map((item) => {
            const Icon = item.icon;
            const active = isPathActive(location.pathname, item);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={`flex min-h-11 flex-col items-center justify-center gap-1 rounded-lg px-1 py-1 text-[11px] font-medium ${
                  active ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
                }`}
              >
                <Icon className="h-4 w-4" strokeWidth={1.75} />
                <span className="truncate">{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
      <SupportDownloadRail telegramUrl={telegramSupportUrl} androidUrl={androidAppUrl} iosUrl={iosGuideUrl} />
    </div>
  );
}
